import { supabase } from './supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { generateRandomName } from '../utils/nameGenerator';
import type { PeerData, DiscoveryMethod } from '../types';

/**
 * Local Discovery service for network-based peer discovery
 * Uses hashed network ID (based on public IP) to group peers
 */
export class LocalDiscoveryService {
  private channel: RealtimeChannel | null = null;
  private peerId: string;
  private peerName: string;
  private networkId: string | null = null;
  private discoveredPeers: Map<string, PeerData> = new Map();

  // Callbacks
  private onPeersChanged?: (peers: PeerData[]) => void;
  private onOfferReceived?: (peerId: string, offer: RTCSessionDescriptionInit) => void;
  private onAnswerReceived?: (peerId: string, answer: RTCSessionDescriptionInit) => void;

  constructor() {
    this.peerId = this.generatePeerId();
    this.peerName = generateRandomName();
    console.log('🔍 Local discovery service initialized:', this.peerName);
  }

  /**
   * Generate unique peer ID
   */
  private generatePeerId(): string {
    return `peer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get network ID from Edge Function
   */
  private async getNetworkId(): Promise<string | null> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      // Use local Supabase functions URL if in development
      const isDevelopment = import.meta.env.DEV;
      const functionsUrl = isDevelopment 
        ? 'http://localhost:54321/functions/v1/get-network-id'
        : `${supabaseUrl}/functions/v1/get-network-id`;
      
      console.log('Calling Edge Function:', functionsUrl);
      
      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Edge Function error:', response.status, errorText);
        throw new Error(`Failed to get network ID: ${response.status}`);
      }

      const data = await response.json();
      return data.networkId;
    } catch (error) {
      console.error('Error getting network ID:', error);
      return null;
    }
  }

  /**
   * Enable local discovery and join network channel
   */
  async enable(customPeerName?: string): Promise<boolean> {
    if (customPeerName) {
      this.peerName = customPeerName;
    }

    // Get network ID
    this.networkId = await this.getNetworkId();
    
    if (!this.networkId) {
      console.error('❌ Failed to get network ID');
      return false;
    }

    console.log('🌐 Network ID obtained, joining channel...');

    // Leave existing channel if any
    if (this.channel) {
      await this.disable();
    }

    // Create channel for this network
    const channelName = `local:${this.networkId}`;
    this.channel = supabase.channel(channelName, {
      config: {
        presence: {
          key: this.peerId,
        },
      },
    });

    // Track presence - who's in the network
    this.channel
      .on('presence', { event: 'sync' }, () => {
        const state = this.channel?.presenceState();
        if (!state) return;

        // Update discovered peers
        this.discoveredPeers.clear();
        Object.keys(state).forEach(peerId => {
          if (peerId !== this.peerId) {
            const peerPresence = state[peerId];
            const peerData = peerPresence?.[0] as any;
            this.discoveredPeers.set(peerId, {
              id: peerId,
              name: peerData?.peer_name || 'Unknown Peer',
              connected: false,
              discoveryMethod: 'local' as DiscoveryMethod,
            });
          }
        });

        console.log('👥 Discovered peers:', this.discoveredPeers.size);
        this.notifyPeersChanged();
      })
      .on('presence', { event: 'join' }, ({ key }) => {
        console.log('➕ Peer joined:', key);
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        console.log('➖ Peer left:', key);
        this.discoveredPeers.delete(key);
        this.notifyPeersChanged();
      });

    // Listen for WebRTC signals
    this.channel
      .on('broadcast', { event: 'local-offer' }, ({ payload }) => {
        if (payload.to === this.peerId) {
          console.log('📥 Received local offer from:', payload.from);
          this.onOfferReceived?.(payload.from, payload.offer);
        }
      })
      .on('broadcast', { event: 'local-answer' }, ({ payload }) => {
        if (payload.to === this.peerId) {
          console.log('📥 Received local answer from:', payload.from);
          this.onAnswerReceived?.(payload.from, payload.answer);
        }
      });

    // Subscribe and track presence
    await this.channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await this.channel?.track({
          peer_id: this.peerId,
          peer_name: this.peerName,
          joined_at: new Date().toISOString(),
        });
        console.log('✅ Connected to local discovery channel as:', this.peerName);
      }
    });

    return true;
  }

  /**
   * Disable local discovery and leave channel
   */
  async disable(): Promise<void> {
    if (this.channel) {
      try {
        await this.channel.untrack();
        await this.channel.unsubscribe();
      } catch (err) {
        console.warn('Error during channel cleanup:', err);
      }
      this.channel = null;
    }
    this.discoveredPeers.clear();
    this.networkId = null;
    this.notifyPeersChanged();
    console.log('👋 Left local discovery channel');
  }

  /**
   * Send offer to a specific peer
   */
  async sendOffer(peerId: string, offer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.channel) {
      throw new Error('Not connected to local discovery channel');
    }

    console.log('📤 Sending local offer to:', peerId);
    await this.channel.send({
      type: 'broadcast',
      event: 'local-offer',
      payload: {
        from: this.peerId,
        to: peerId,
        offer,
      },
    });
  }

  /**
   * Send answer to a specific peer
   */
  async sendAnswer(peerId: string, answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.channel) {
      throw new Error('Not connected to local discovery channel');
    }

    console.log('📤 Sending local answer to:', peerId);
    await this.channel.send({
      type: 'broadcast',
      event: 'local-answer',
      payload: {
        from: this.peerId,
        to: peerId,
        answer,
      },
    });
  }

  /**
   * Update peer name
   */
  async updatePeerName(newName: string): Promise<void> {
    this.peerName = newName;
    console.log('✏️ Updated local discovery peer name to:', newName);
    
    // Update presence with new name
    if (this.channel) {
      await this.channel.track({
        peer_id: this.peerId,
        peer_name: this.peerName,
        joined_at: new Date().toISOString(),
      });
    }
  }

  /**
   * Notify listeners of peer changes
   */
  private notifyPeersChanged(): void {
    const peers = Array.from(this.discoveredPeers.values());
    this.onPeersChanged?.(peers);
  }

  /**
   * Set callback handlers
   */
  setCallbacks(callbacks: {
    onPeersChanged?: (peers: PeerData[]) => void;
    onOfferReceived?: (peerId: string, offer: RTCSessionDescriptionInit) => void;
    onAnswerReceived?: (peerId: string, answer: RTCSessionDescriptionInit) => void;
  }): void {
    this.onPeersChanged = callbacks.onPeersChanged;
    this.onOfferReceived = callbacks.onOfferReceived;
    this.onAnswerReceived = callbacks.onAnswerReceived;
  }

  /**
   * Get discovered peers
   */
  getDiscoveredPeers(): PeerData[] {
    return Array.from(this.discoveredPeers.values());
  }

  /**
   * Get peer by ID
   */
  getPeerById(peerId: string): PeerData | undefined {
    return this.discoveredPeers.get(peerId);
  }

  /**
   * Check if enabled
   */
  isEnabled(): boolean {
    return this.channel !== null;
  }

  /**
   * Get peer ID
   */
  getPeerId(): string {
    return this.peerId;
  }

  /**
   * Get peer name
   */
  getPeerName(): string {
    return this.peerName;
  }
}

export const localDiscoveryService = new LocalDiscoveryService();
